import { ComponentProps, MouseEvent, ReactNode, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { MenuIcon } from '@karnott/icons';
import { spacing } from '@karnott/theme';
import { Button } from './button';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row-reverse;
  .button:not(:last-of-type) {
    position: absolute;
  }
  .button {
    transition: all 0.25s ease-in-out;
  }
`;

type Props = {
  /** The buttons to display */
  buttons: ReactNode[];
  /** Props to pass to the expand button */
  expandButtonProps?: ComponentProps<typeof Button>;
  /** The gap between buttons */
  gap?: number;
  /** Callback when the buttons are opened */
  onExpand?: () => void;
  /** Callback when the buttons are closed */
  onShrink?: () => void;
};

/** An expandable horizontal button group */
export function ExpandableButton({ buttons, expandButtonProps, gap = spacing('medium'), onExpand, onShrink }: Props) {
  const [expanded, setExpanded] = useState(false);

  const expandButton = useRef<HTMLDivElement>(null);
  const buttonSize = expandButton.current?.offsetWidth;

  const onExpandClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      expandButtonProps?.onClick?.(e);
      expanded ? onShrink?.() : onExpand?.();
      setExpanded(!expanded);
    },
    [expandButtonProps, expanded, onShrink, onExpand],
  );

  return (
    <Wrapper>
      {buttons?.map((button, index) => (
        <div
          className="button"
          style={{ right: expanded ? (index + 1) * ((buttonSize || 0) + gap) : 0, opacity: expanded ? 1 : 0 }}
          key={index}
        >
          {button}
        </div>
      ))}
      <div className="button" ref={expandButton}>
        <Button
          large
          primary
          outlinedNeutral={!expanded}
          Icon={MenuIcon}
          {...expandButtonProps}
          onClick={onExpandClick}
        />
      </div>
    </Wrapper>
  );
}
